import { ConversationRow, Spinner } from '../design-system';
import { STAGE_DOT_COLOR, type HostConversationSummary } from '../../lib/hostConversations';
import { cn } from '../../lib/utils';

/**
 * ConversationList — the host inbox rows, rendered with the design-system
 * ConversationRow: identity avatar, name + time, trip line with a
 * stage-coloured dot, preview with unread badge, gold-keyline selection.
 */

function initialsOf(name: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase();
}

/** Compact relative-time label for conversation-list rows (e.g. "5m", "3h", "2d"). */
function relativeShort(iso: string): string {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return 'now';
  if (mins < 60) return `${mins}m`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}

function previewOf(c: HostConversationSummary, hostId: string): string {
  const m = c.lastMessage;
  if (!m) return 'No messages yet';
  if (m.role === 'system') return m.content;
  return (m.sender === hostId ? 'You: ' : '') + m.content;
}

export interface ConversationListProps {
  conversations: HostConversationSummary[];
  activeMatchId?: string;
  hostId: string;
  onSelect: (matchId: string) => void;
  loading?: boolean;
  className?: string;
}

export function ConversationList({ conversations, activeMatchId, hostId, onSelect, loading, className }: ConversationListProps) {
  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center gap-2 px-6 py-12 text-center">
        <i className="ti ti-message-off text-[24px] text-app-muted" />
        <p className="text-[12.5px] text-app-muted" style={{ textWrap: 'pretty' }}>No conversations match that.</p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col px-2 pb-4', className)}>
      {conversations.map((c) => (
        <ConversationRow
          key={c.matchId}
          name={c.travelerName ?? 'Traveler'}
          initials={initialsOf(c.travelerName)}
          colorSeed={c.travelerId}
          avatarUrl={c.travelerAvatar}
          time={relativeShort(c.updatedAt)}
          trip={c.tripName ?? undefined}
          statusColor={STAGE_DOT_COLOR[c.stage]}
          preview={previewOf(c, hostId)}
          unreadCount={c.unread ? 1 : 0}
          selected={c.matchId === activeMatchId}
          onClick={() => onSelect(c.matchId)}
        />
      ))}
    </div>
  );
}

export default ConversationList;
